import {
    insertCompletionText,
    startCompletion,
    type Completion,
    type CompletionContext,
    type CompletionResult,
    type CompletionSource,
} from "@codemirror/autocomplete";
import type { EditorState } from "@codemirror/state";
import type { EditorView } from "@codemirror/view";
import type { IdfTargets } from "idfweb-build";

import manifestSchema from "./idf-component.schema.json" with { type: "json" };

/** The parts of JSON Schema that `idf-component.schema.json` uses. */
export interface Schema {
    description?: string;
    type?: string;
    properties?: Record<string, Schema>;
    additionalProperties?: Schema | boolean;
    items?: Schema;
    oneOf?: Schema[];
    enum?: unknown[];
    const?: unknown;
    $ref?: string;
    deprecated?: boolean;
    $defs?: Record<string, Schema>;
}

const ROOT = manifestSchema as Schema;

/**
 * Completion in `idf_component.yml`: keys and values from the manifest schema,
 * target names in `targets` and in `if` clauses, and the left-hand sides of an
 * `if` clause. `targets` gives the bundled ESP-IDF's chips, which the asset
 * index lists, so they are known before ESP-IDF itself is downloaded.
 */
export function manifestCompletion(targets: () => IdfTargets | undefined): CompletionSource {
    return (context) => {
        const where = manifestContext(context.state, context.pos);
        if (where === undefined) return null;
        const word = where.text.match(/[\w$./-]*$/)![0];
        if (word === "" && !context.explicit && where.kind === "key") return null;
        const from = context.pos - word.length;
        const before = where.text.slice(0, where.text.length - word.length);
        const node = schemaAt(where.path);

        let options: Completion[] = [];
        if (where.kind === "key" && node !== undefined && isObject(node)) {
            options = keyOptions(node, where, stepOf(context.state));
        } else if (where.path.at(-1) === "if") {
            options = conditionOptions(before, targets());
        } else if (where.path.join(".") === "targets.-") {
            options = targetOptions(targets());
        } else if (node !== undefined) {
            options = valueOptions(node);
        }
        if (options.length === 0) return null;
        return { from, options, validFor: /^[\w$./-]*$/ } satisfies CompletionResult;
    };
}

// ---- where the cursor is ----------------------------------------------------

export interface ManifestContext {
    /** Keys from the root to the node at the cursor; "-" stands for a sequence item. */
    path: string[];
    /** A key being typed, or a value: a scalar after `key: `, a sequence item, a flow sequence item. */
    kind: "key" | "value";
    /** The line (for a key) or the value (for a value) before the cursor. */
    text: string;
    /** The column a key typed here starts at. */
    column: number;
    /** Keys already in the mapping the cursor is in. */
    siblings: Set<string>;
}

interface Node {
    column: number;
    /** A mapping key; undefined for a sequence item's dash or a scalar. */
    key?: string;
    dash?: boolean;
}

/**
 * The YAML path at `pos`, from indentation: block mappings and sequences as
 * manifests are written, and a flow sequence on the cursor's line. Undefined
 * in a comment or a multi-line scalar.
 */
export function manifestContext(state: EditorState, pos: number): ManifestContext | undefined {
    const doc = state.doc;
    const line = doc.lineAt(pos);
    const before = line.text.slice(0, pos - line.from);
    if (commentStart(before) >= 0) return undefined;

    const head = /^(\s*)((?:-\s+)*)/.exec(before)!;
    const rest = before.slice(head[0].length);
    const dashes = [...head[2]!.matchAll(/-\s*/g)].map((m) => head[1]!.length + m.index);
    const column = head[0].length;
    const path = ancestors(state, line.number, dashes[0] ?? column, dashes.length > 0);
    path.push(...dashes.map(() => "-"));

    const pair = /^("[^"]*"|'[^']*'|[^\s#"'][^#]*?)\s*:\s(\s*)(.*)$/.exec(rest);
    if (pair !== null) {
        path.push(unquote(pair[1]!));
        let value = pair[3]!;
        if (value.startsWith("[")) {
            if (/\]/.test(value)) return undefined;
            path.push("-");
            value = value.slice(value.lastIndexOf(value.includes(",") ? "," : "[") + 1).trimStart();
        }
        return { path, kind: "value", text: value.replace(/^["']/, ""), column, siblings: new Set() };
    }
    if (/:/.test(rest) || /\s/.test(rest.trim())) return undefined;
    // A partial word: a key, or a scalar sequence item, which the schema tells apart.
    if (dashes.length > 0 && !isObject(schemaAt(path))) {
        return { path, kind: "value", text: rest.replace(/^["']/, ""), column, siblings: new Set() };
    }
    return { path, kind: "key", text: rest, column, siblings: siblings(state, line.number, column, dashes.length > 0) };
}

/** The keys and sequence items enclosing a node that starts at `column` on line `number`. */
function ancestors(state: EditorState, number: number, column: number, dash: boolean): string[] {
    const path: string[] = [];
    for (let n = number - 1; n >= 1 && (column > 0 || dash); n--) {
        const nodes = lineNodes(state.doc.line(n).text);
        if (nodes === undefined) continue;
        let outer = nodes.filter((node) => node.column < column);
        // A block sequence may start at its key's column.
        const last = nodes.at(-1)!;
        if (dash && outer.length === nodes.length - 1 && last.column === column && last.key !== undefined) {
            outer = nodes;
        }
        if (outer.length === 0) continue;
        path.unshift(...outer.map((node) => (node.dash ? "-" : (node.key ?? "-"))));
        column = outer[0]!.column;
        dash = outer[0]!.dash === true;
    }
    return path;
}

/** The keys at `column` in the mapping around line `number`. */
function siblings(state: EditorState, number: number, column: number, startsItem: boolean): Set<string> {
    const keys = new Set<string>();
    const at = (nodes: Node[]) => nodes.find((node) => node.column === column && node.key !== undefined)?.key;
    for (let n = number - 1; n >= 1 && !startsItem; n--) {
        const nodes = lineNodes(state.doc.line(n).text);
        if (nodes === undefined) continue;
        const key = at(nodes);
        if (key !== undefined) keys.add(key);
        if (nodes[0]!.column < column) break;
    }
    for (let n = number + 1; n <= state.doc.lines; n++) {
        const nodes = lineNodes(state.doc.line(n).text);
        if (nodes === undefined) continue;
        if (nodes[0]!.column < column) break;
        const key = nodes[0]!.column === column ? at(nodes) : undefined;
        if (key !== undefined) keys.add(key);
    }
    return keys;
}

/** A line's sequence dashes and its key, by column; undefined for a blank or comment line. */
function lineNodes(text: string): Node[] | undefined {
    const start = commentStart(text);
    const content = start < 0 ? text : text.slice(0, start);
    if (content.trim() === "") return undefined;
    const head = /^(\s*)((?:-(?:\s+|$))*)/.exec(content)!;
    const nodes: Node[] = [...head[2]!.matchAll(/-\s*/g)].map((m) => ({
        column: head[1]!.length + m.index,
        dash: true,
    }));
    const pair = /^("[^"]*"|'[^']*'|[^\s#"'][^#]*?)\s*:(\s|$)/.exec(content.slice(head[0].length));
    if (pair !== null) nodes.push({ column: head[0].length, key: unquote(pair[1]!) });
    else if (content.length > head[0].length) nodes.push({ column: head[0].length });
    return nodes;
}

/** Where a `#` comment starts in a line, outside quotes; -1 if it has none. */
function commentStart(text: string): number {
    let quote = "";
    for (let i = 0; i < text.length; i++) {
        const char = text[i]!;
        if (quote !== "") {
            if (char === quote) quote = "";
        } else if (char === '"' || char === "'") {
            quote = char;
        } else if (char === "#" && (i === 0 || /\s/.test(text[i - 1]!))) {
            return i;
        }
    }
    return -1;
}

function unquote(key: string): string {
    return /^(["']).*\1$/.test(key) ? key.slice(1, -1) : key;
}

/** The document's indentation step: its smallest indent, or 2. */
function stepOf(state: EditorState): number {
    let step = Infinity;
    for (const line of state.doc.iterLines()) {
        const indent = /^ */.exec(line)![0].length;
        if (indent > 0 && indent < line.length && !line.trimStart().startsWith("#")) step = Math.min(step, indent);
    }
    return step === Infinity ? 2 : step;
}

// ---- the schema --------------------------------------------------------------

/** The schema of the node at `path`, or undefined where the schema says nothing. */
export function schemaAt(path: string[], schema: Schema = ROOT): Schema | undefined {
    let node: Schema | undefined = schema;
    for (const segment of path) {
        node = node === undefined ? undefined : child(node, segment);
    }
    return node;
}

function child(schema: Schema, segment: string): Schema | undefined {
    for (const alt of alternatives(schema)) {
        if (segment === "-") {
            if (alt.items !== undefined) return deref(alt.items);
            continue;
        }
        const property = alt.properties?.[segment];
        if (property !== undefined) return deref(property);
        if (typeof alt.additionalProperties === "object") return deref(alt.additionalProperties);
    }
    return undefined;
}

function deref(schema: Schema): Schema {
    const name = schema.$ref?.replace("#/$defs/", "");
    return name === undefined ? schema : ROOT.$defs![name]!;
}

/** A schema's alternatives: each `oneOf` branch, or the schema itself. */
function alternatives(schema: Schema): Schema[] {
    const own = deref(schema);
    return own.oneOf === undefined ? [own] : own.oneOf.flatMap(alternatives);
}

function isObject(schema: Schema | undefined): boolean {
    return schema !== undefined && alternatives(schema).some((alt) => alt.type === "object");
}

function keyOptions(schema: Schema, where: ManifestContext, step: number): Completion[] {
    const options: Completion[] = [];
    for (const alt of alternatives(schema)) {
        for (const [key, property] of Object.entries(alt.properties ?? {})) {
            if (where.siblings.has(key)) continue;
            const value = deref(property);
            const kinds = new Set(alternatives(value).map((a) => a.type));
            const indent = "\n" + " ".repeat(where.column + step);
            const insert =
                kinds.size === 1 && kinds.has("object")
                    ? `${key}:${indent}`
                    : kinds.size === 1 && kinds.has("array")
                      ? `${key}:${indent}- `
                      : `${key}: `;
            const more = kinds.has("object") || kinds.has("array") || valueOptions(value).length > 0 || key === "if";
            options.push({
                label: key,
                type: "property",
                detail: value.deprecated === true ? "deprecated" : undefined,
                boost: value.deprecated === true ? -1 : undefined,
                info: () => documentation(property.description ?? value.description),
                apply: (view: EditorView, _completion: Completion, from: number, to: number) => {
                    view.dispatch(insertCompletionText(view.state, insert, from, to));
                    if (more) startCompletion(view);
                },
            });
        }
    }
    return options;
}

/** Enumerated and boolean values. */
function valueOptions(schema: Schema): Completion[] {
    const values = new Set<string>();
    for (const alt of alternatives(schema)) {
        for (const value of alt.enum ?? []) values.add(String(value));
        if (alt.const !== undefined) values.add(String(alt.const));
        else if (alt.type === "boolean") values.add("true").add("false");
    }
    return [...values].map((label) => ({ label, type: /^(true|false)$/.test(label) ? "keyword" : "enum" }));
}

// ---- if clauses and targets ------------------------------------------------------

/** Supported targets, then preview ones. */
function targetOptions(targets: IdfTargets | undefined): Completion[] {
    return [
        ...(targets?.supported ?? []).map((label) => ({ label, type: "constant" })),
        ...(targets?.preview ?? []).map((label) => ({ label, type: "constant", detail: "preview" })),
    ];
}

const LEFT_VALUES: Completion[] = [
    {
        label: "target",
        type: "keyword",
        info: () =>
            documentation(
                "The chip the project is built for. Compare it with `==`, `!=`, `in [...]` or `not in [...]`.",
            ),
    },
    {
        label: "idf_version",
        type: "keyword",
        info: () => documentation("The ESP-IDF version, followed by a version range such as `>=5.0`."),
    },
    {
        label: "$CONFIG{",
        type: "variable",
        info: () =>
            documentation(
                "A Kconfig option without its `CONFIG_` prefix, such as `$CONFIG{FREERTOS_HZ}`, from ESP-IDF or a direct dependency. ESP-IDF v5.5 and later.",
            ),
    },
];

/** Completion in an `if` clause, given its text before the word at the cursor. */
function conditionOptions(before: string, targets: IdfTargets | undefined): Completion[] {
    if (/\btarget\s*(==|!=)\s*["']?$/.test(before) || /\btarget\s+(not\s+)?in\s*\[[^\]]*$/.test(before)) {
        return targetOptions(targets);
    }
    if (/(^|&&|\|\||\()\s*$/.test(before)) return LEFT_VALUES;
    return [];
}

/** A description, with `code` spans. */
function documentation(text: string | undefined): HTMLElement | null {
    if (text === undefined) return null;
    const element = document.createElement("div");
    element.className = "wb-completion-prose";
    text.split("`").forEach((part, i) => {
        if (i % 2 === 0) {
            element.append(part);
        } else {
            const code = document.createElement("code");
            code.textContent = part;
            element.append(code);
        }
    });
    return element;
}
